/**
 * Staff token claims as the API's `StaffJwtGuard` reads them (apps/api/src/auth/staff-jwt.verifier.ts):
 * Staff_Roles from `realm_access.roles` (Keycloak defaults and unknown roles dropped),
 * `tenant_ids` / `outlet_ids` as lower-case id arrays (absent claim = none).
 *
 * Clients only decode the payload to drive the UI; the API verifies the signature, so a
 * tampered token gains nothing here.
 */
import { STAFF_ROLES } from '@macksons/contracts';
/** Decodes a JWT's payload without verifying it. Returns `undefined` when malformed. */
export function decodeJwtPayload(token) {
    const part = token.split('.')[1];
    if (!part)
        return undefined;
    try {
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
        const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        return typeof payload === 'object' && payload !== null && !Array.isArray(payload)
            ? payload
            : undefined;
    }
    catch {
        return undefined;
    }
}
function idArray(value) {
    const raw = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
    return [...new Set(raw.filter((v) => typeof v === 'string' && v !== '').map((v) => v.toLowerCase()))];
}
const optStr = (v) => (typeof v === 'string' && v !== '' ? v : undefined);
export function parseStaffClaims(payload) {
    const realmAccess = payload['realm_access'];
    const rawRoles = typeof realmAccess === 'object' && realmAccess !== null && Array.isArray(realmAccess.roles)
        ? (realmAccess.roles)
        : [];
    const username = optStr(payload['preferred_username']);
    return {
        sub: optStr(payload['sub']) ?? '',
        roles: STAFF_ROLES.filter((r) => rawRoles.includes(r)),
        tenantIds: idArray(payload['tenant_ids']),
        outletIds: idArray(payload['outlet_ids']),
        name: optStr(payload['name']) ?? username,
        username,
        email: optStr(payload['email']),
    };
}
/** Claims of an access token, or `undefined` when it cannot be decoded. */
export function staffClaimsFromAccessToken(accessToken) {
    const payload = decodeJwtPayload(accessToken);
    return payload ? parseStaffClaims(payload) : undefined;
}
export function hasAnyRole(claims, roles) {
    return !!claims && claims.roles.some((r) => roles.includes(r));
}
//# sourceMappingURL=staff-claims.js.map